import type { PasswordRequest } from '@/types';

import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/navigation';

import { GoBackButton } from '@/app/auth/sign-up/_components';
import { Box, Button, PasswordInput, Stack } from '@/components';
import { useInputAttributes, useSetPassword, useTranslation } from '@/hooks';
import { routes } from '@/statics';
import {
  passwordValidator,
  samePasswordValidator,
  yupValidator,
} from '@/utils';

type PasswordFormData = Pick<PasswordRequest, 'password' | 'password_repeat'>;

type PasswordFormProps = {
  mobile: string;
  csrfToken: string;
};

const PasswordForm: React.FC<PasswordFormProps> = ({ mobile, csrfToken }) => {
  const router = useRouter();
  const [navigateLoading, setNavigateLoading] = React.useState<boolean>(false);

  const { t } = useTranslation('pages.forgetPassword.form.passwordForm');

  const validationSchema = yupValidator.object().shape({
    password: passwordValidator({ label: t('password') }),
    password_repeat: samePasswordValidator({ label: t('password_repeat') }),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const filedInputProps = useInputAttributes<PasswordFormData>(
    control,
    register,
    errors,
    {
      password: t('password'),
      password_repeat: t('password_repeat'),
    }
  );

  const { mutateAsync: setPassword, isPending: setPasswordLoading } =
    useSetPassword();

  const onSubmit = (data: PasswordFormData) => {
    setNavigateLoading(true);
    void setPassword({ ...data, token: csrfToken, mobile })
      .then(() => {
        router.replace(routes.signIn.url);
      })
      .catch(() => setNavigateLoading(false));
  };

  return (
    <Stack
      w="100%"
      h="100%"
      align="flex-start"
      gap={4}
      pt={4}
      display="flex"
      flexDir="column"
      flex={1}
      as="form"
    >
      <Box gap={4} w="full" display="flex" flexDirection="column">
        <PasswordInput hasHint {...filedInputProps.password} />
        <PasswordInput {...filedInputProps.password_repeat} />
      </Box>
      <Stack w="full" align="center" gap={6} justify="end" flexGrow={1}>
        <Button
          type="submit"
          w="full"
          onClick={(data) => void handleSubmit(onSubmit)(data)}
          isLoading={setPasswordLoading || navigateLoading}
        >
          {t('submitPassword')}
        </Button>
        <GoBackButton />
      </Stack>
    </Stack>
  );
};

export default PasswordForm;
